import { getPathWithRootPath } from '../Services/common';
import { ModuleStyle } from '../Types/containerV2';

export type CSSVariables = {
	[key: `--${string}`]: string | number;
};

function formatBackgroundPosition(value: string | undefined, fallback: number): string {
	if (!value?.trim()) return `${fallback}%`;

	const trimmedValue = value.trim();
	return /^-?\d+(?:\.\d+)?$/.test(trimmedValue) ? `${trimmedValue}%` : trimmedValue;
}

export function mapStyleToBackgroundImageVars(style?: ModuleStyle): CSSVariables {
	if (!style?.background?.imageUrl) return {};

	const vars: CSSVariables = {
		'--background-image-url': `url(${getPathWithRootPath(style.background.imageUrl)})`,
		'--background-position-x': formatBackgroundPosition(style.background.positionX, 50),
		'--background-position-y': formatBackgroundPosition(style.background.positionY, 50),
		'--background-size': style.background.size ?? 'cover',
		'--background-zoom': (style.background.zoom ?? 100) / 100,
		'--background-brightness': (style.background.brightness ?? 100) + '%',
		'--background-blur': (style.background.blur ?? 0) + 'px',
		'--background-opacity': (style.background.opacity ?? 100) + '%',
	};
	return vars;
}

export function mapStyleToVarsAndClasses(
	style?: ModuleStyle,
	mobileStyle?: ModuleStyle
): { vars: CSSVariables; classes: string[] } {
	const baseVars = __mapStyleToVars(style);
	const mobileVars = __mapStyleToVars(mobileStyle, '-mobile');

	if (style?.desktopWidthClass) baseVars.classes.push(style.desktopWidthClass);
	if (mobileStyle?.mobileWidthClass) mobileVars.classes.push(mobileStyle.mobileWidthClass);
	if (style?.hidden === 'desktop' || style?.hidden === 'both') baseVars.classes.push('hidden-desktop');
	if (style?.hidden === 'mobile' || style?.hidden === 'both') mobileVars.classes.push('hidden-mobile');

	return { vars: { ...baseVars.vars, ...mobileVars.vars }, classes: [...baseVars.classes, ...mobileVars.classes] };
}

function __mapStyleToVars(style?: ModuleStyle, suffix: string = ''): { vars: CSSVariables; classes: string[] } {
	if (!style) return { vars: {}, classes: [] };

	const vars: CSSVariables = {};
	const classes: string[] = [];

	if (style.spacing) {
		if (style.spacing.padding) {
			vars[`--padding${suffix}`] = style.spacing.padding;
			classes.push(`has-padding${suffix}`);
		}

		if (style.spacing.margin) {
			vars[`--margin${suffix}`] = style.spacing.margin;
			classes.push(`has-margin${suffix}`);
		}
	}

	if (style.size) {
		if (style.size.width) {
			vars[`--width${suffix}`] = style.size.width;
			classes.push(`has-width${suffix}`);
		}
		if (style.size.maxWidth) {
			vars[`--max-width${suffix}`] = style.size.maxWidth;
			classes.push(`has-max-width${suffix}`);
		}
		if (style.size.minWidth) {
			vars[`--min-width${suffix}`] = style.size.minWidth;
			classes.push(`has-min-width${suffix}`);
		}
		if (style.size.minHeight) {
			vars[`--min-height${suffix}`] = style.size.minHeight;
			classes.push(`has-min-height${suffix}`);
		}
		if (style.size.maxHeight) {
			vars[`--max-height${suffix}`] = style.size.maxHeight;
			classes.push(`has-max-height${suffix}`);
		}
	}

	if (style.text) {
		if (style.text.color) {
			vars[`--text-color${suffix}`] = style.text.color;
			classes.push(`has-text-color${suffix}`);
		}
		if (style.text.style) {
			vars[`--text-style${suffix}`] = style.text.style;
			classes.push(`has-text-style${suffix}`);
		}
		if (style.text.lineHeight) {
			vars[`--text-line-height${suffix}`] = style.text.lineHeight;
			classes.push(`has-text-line-height${suffix}`);
		}

		if (style.text.size) {
			vars[`--text-size${suffix}`] = style.text.size;
			classes.push(`has-text-size${suffix}`);
		}

		if (style.text.weight) {
			vars[`--text-weight${suffix}`] = style.text.weight;
			classes.push(`has-text-weight${suffix}`);
		}

		if (style.text.align) {
			vars[`--text-align${suffix}`] = style.text.align;
			classes.push(`has-text-align${suffix}`);
		}

		if (style.text.fontFamily) {
			vars[`--text-font-family${suffix}`] = style.text.fontFamily;
			classes.push(`has-text-font-family${suffix}`);
		}
	}

	if (style.border) {
		if (style.border.width) {
			vars[`--border-width${suffix}`] = style.border.width;
			classes.push(`has-border-width${suffix}`);
		}
		if (style.border.color) {
			vars[`--border-color${suffix}`] = style.border.color;
			classes.push(`has-border-color${suffix}`);
		}
		if (style.border.style) {
			vars[`--border-style${suffix}`] = style.border.style;
			classes.push(`has-border-style${suffix}`);
		}
		if (style.border.radius) {
			vars[`--border-radius${suffix}`] = style.border.radius;
			classes.push(`has-border-radius${suffix}`);
		}
	}

	if (style.background) {
		vars[`--background-brightness${suffix}`] = (style.background.brightness ?? 100) + '%';
		vars[`--background-blur${suffix}`] = (style.background.blur ?? 0) + 'px';
		vars[`--background-opacity${suffix}`] = (style.background.opacity ?? 100) + '%';

		if (style.background.color) {
			vars[`--background-color${suffix}`] = style.background.color;
			classes.push(`has-background-color${suffix}`);
		}
	}

	if (style.hover) {
		if (style.hover.color) {
			vars[`--hover-color${suffix}`] = style.hover.color;
			classes.push(`has-hover-color${suffix}`);
		}
		if (style.hover.decoration) {
			vars[`--hover-decoration${suffix}`] = style.hover.decoration;
			classes.push(`has-hover-decoration${suffix}`);
		}
	}

	if (style.effects) {
		if (style.effects.reveal?.type) {
			vars[`--effect-reveal-duration${suffix}`] = style.effects.reveal.duration || '600ms';
			vars[`--effect-reveal-delay${suffix}`] = style.effects.reveal.delay || '0ms';

			if (style.effects.reveal.distance) {
				vars[`--effect-reveal-distance${suffix}`] = style.effects.reveal.distance;
			}

			if (style.effects.reveal.scale != null) {
				vars[`--effect-reveal-scale${suffix}`] = style.effects.reveal.scale;
			}

			classes.push(`has-effect-reveal-${style.effects.reveal.type}${suffix}`);

			if (style.effects.reveal.type === 'move' && style.effects.reveal.direction) {
				classes.push(`has-effect-reveal-move-${style.effects.reveal.direction}${suffix}`);
			}
		}

		if (style.effects.hover) {
			vars[`--effect-hover-duration${suffix}`] = style.effects.hover.duration || '200ms';
			classes.push(`has-effect-hover${suffix}`);

			if (style.effects.hover.move?.distance) {
				vars[`--effect-hover-move-distance${suffix}`] = style.effects.hover.move.distance;
			}

			if (style.effects.hover.move?.direction) {
				classes.push(`has-effect-hover-move-${style.effects.hover.move.direction}${suffix}`);
			}

			if (style.effects.hover.shadow) {
				classes.push(`has-effect-hover-shadow${suffix}`);
			}

			if (style.effects.hover.scale != null) {
				vars[`--effect-hover-scale${suffix}`] = style.effects.hover.scale;
				classes.push(`has-effect-hover-scale${suffix}`);
			}

			if (style.effects.hover.textColor) {
				vars[`--effect-hover-text-color${suffix}`] = style.effects.hover.textColor;
				classes.push(`has-effect-hover-text-color${suffix}`);
			}
		}
	}

	return {
		vars,
		classes,
	};
}
